import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Chart from 'react-apexcharts';
import Loader from '../../../../component/common/Loader';
import { minsToHhMm } from '../../../SleepHub/sleepHubApi';
import { fetchCompanyShuruUserDetail } from './companyShuruApi';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  MiniDonutCard,
  PANEL_ROW_CLASS,
  Chip,
  EntityHeader,
  BackLink,
  STAT_ICONS,
  CHART_BASE,
  CHART_COLORS,
  CHART_GRID,
  toneChip
} from '../../../../component/common/designSystem';
import { FEEDBACK_RAMP, moodColors } from './shuruTheme';

/**
 * Shuru → User profile.
 *
 * One person's use of the AI companion: how often they reach out, for how long,
 * how they said they felt afterwards, and the mood they arrive in.
 *
 * What is deliberately NOT here: anything they said. No messages, no summaries,
 * and none of the workplace themes their words matched — those exist only as
 * company-wide counts on the dashboard, where they cannot be traced back to a
 * person. A company admin can see that someone uses Shuru; the contents of the
 * conversation stay between the user and the product.
 *
 * `fetcher`/`backPath`/`backLabel` are props so a super-admin Shuru hub can
 * reuse the page against the unscoped endpoint.
 */
const ShuruUserDetail = ({ fetcher = fetchCompanyShuruUserDetail, backPath, backLabel }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId');

  const [loader, setLoader] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!userId) {
      navigate(backPath, { replace: true });
      return;
    }
    let mounted = true;
    setLoader(true);
    fetcher(userId).then((data) => {
      if (!mounted) return;
      setDetail(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, fetcher]);

  const user = detail?.user;
  const daily = detail?.dailyActivity || [];
  const feedbackSplit = detail?.feedbackSplit || [];
  const moodCheckIns = detail?.moodCheckIns || [];

  // Oldest-first so the chart reads left to right, while the table below stays
  // most-recent-first the way every other history list in the panel does.
  const chartDays = [...daily].reverse();

  const activityOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [CHART_COLORS.brand, CHART_COLORS.teal],
    plotOptions: { bar: { borderRadius: 3, columnWidth: '55%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    legend: { position: 'top', horizontalAlign: 'right' },
    xaxis: { categories: chartDays.map((day) => day.date) },
    yaxis: [
      { seriesName: 'Messages' },
      { seriesName: 'Minutes', opposite: true, labels: { formatter: (val) => `${val}m` } }
    ],
    tooltip: {
      shared: true,
      y: [
        { formatter: (val) => `${val.toLocaleString()} messages` },
        { formatter: (val) => minsToHhMm(val) }
      ]
    }
  };

  return (
    <div>
      {loader ? <Loader /> : null}

      <BackLink label={backLabel} onClick={() => navigate(backPath)} />

      {user && (
        <EntityHeader
          className="mt-4"
          image={user.user_profile}
          name={user.name}
          subtitle={user.email}
          action={
            <Chip className={toneChip('brand')}>
              {user.accountType === 'B2B' ? `B2B — ${user.company}` : 'B2C'}
            </Chip>
          }
        />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4 mt-6">
        <StatCard icon={STAT_ICONS.plays} title="Chats" value={user ? user.chats : '-'} />
        <StatCard
          icon={STAT_ICONS.logs}
          title="Messages Sent"
          value={user ? user.userMessages : '-'}
        />
        <StatCard
          icon={STAT_ICONS.time}
          title="Time in Conversation"
          value={user ? minsToHhMm(user.totalTimeMins) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.sessions}
          title="Messages per Chat"
          value={user ? user.messagesPerChat : '-'}
        />
        {/* Blank rather than 0% when they never answered the prompt — "didn't
            rate" and "rated it badly" are different things. */}
        <StatCard
          icon={STAT_ICONS.completions}
          title="Felt Better"
          value={user?.feedbackResponses > 0 ? `${user.feltBetterRate}%` : '-'}
        />
        <StatCard icon={STAT_ICONS.streak} title="Days Used" value={user ? user.activeDays : '-'} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        <MiniDonutCard
          title="How They Felt After"
          subtitle="Answers to the end-of-chat prompt"
          data={feedbackSplit}
          colors={FEEDBACK_RAMP}
          unit="answers"
          totalLabel="Rated"
        />
        <MiniDonutCard
          title="How They Arrive"
          subtitle="Mood selected before a chat"
          data={moodCheckIns}
          colors={moodColors(moodCheckIns)}
          unit="check-ins"
          totalLabel="Check-ins"
          legendLimit={4}
        />

        <Panel title="Shuru History" subtitle="First and most recent conversation">
          <PanelTable
            head={
              <>
                <th className="py-2 pr-4 font-medium">Measure</th>
                <th className="py-2 font-medium text-right">Value</th>
              </>
            }
          >
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">First chat</td>
              <td className="py-2 text-right">{user?.firstChatDate || '-'}</td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Last chat</td>
              <td className="py-2 text-right">{user?.lastChatDate || '-'}</td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Avg time per day used</td>
              <td className="py-2 text-right">{user ? minsToHhMm(user.avgDailyMins) : '-'}</td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Chats rated</td>
              <td className="py-2 text-right">{user ? user.feedbackResponses : '-'}</td>
            </tr>
          </PanelTable>
        </Panel>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <Panel title="Recent Activity" subtitle="Messages and time, per day used">
          {chartDays.length > 0 ? (
            <Chart
              type="bar"
              height={320}
              width="100%"
              series={[
                { name: 'Messages', type: 'column', data: chartDays.map((day) => day.messages) },
                { name: 'Minutes', type: 'line', data: chartDays.map((day) => day.minutes) }
              ]}
              options={activityOptions}
            />
          ) : (
            <PanelEmpty>No conversations yet.</PanelEmpty>
          )}
        </Panel>

        {/* Days, not chats: the record has no per-chat duration, so a day is
            the smallest unit that can carry a time. */}
        <Panel title="Chat Days" subtitle="Most recent days this user talked to Shuru">
          {daily.length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Date</th>
                  <th className="py-2 pr-4 font-medium text-right">Chats</th>
                  <th className="py-2 pr-4 font-medium text-right">Messages</th>
                  <th className="py-2 font-medium text-right">Time</th>
                </>
              }
            >
              {daily.map((day) => (
                <tr key={day.date} className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">{day.date}</td>
                  <td className="py-2 pr-4 text-right">{day.chats.toLocaleString()}</td>
                  <td className="py-2 pr-4 text-right">{day.messages.toLocaleString()}</td>
                  <td className="py-2 text-right">{minsToHhMm(day.minutes)}</td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>No conversations yet.</PanelEmpty>
          )}
        </Panel>
      </div>
    </div>
  );
};

ShuruUserDetail.propTypes = {
  fetcher: PropTypes.func,
  backPath: PropTypes.string.isRequired,
  backLabel: PropTypes.string
};

ShuruUserDetail.defaultProps = {
  backLabel: 'Back'
};

export default ShuruUserDetail;
